import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import { Select, TextInput } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { components } from "react-select";
import { usePatchBackgroundJobByIdMutation } from "services/background-job-api";
import type { BackgroundJobDataType } from "types/DataTypes";
import { hasApiError } from "utils/error-utils";
import { useConstants } from "utils/hooks";

import type { OptionProps, SingleValueProps } from "react-select";

type JobTableModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialData?: BackgroundJobDataType;
};

type JobFormData = {
  status: string;
  status_message: string;
};

type StatusOptionType = {
  label: string;
  value: string | null;
  color: string;
};

const StatusOption = (props: OptionProps<StatusOptionType>) => (
  <components.Option {...props}>
    <div className="flex items-center gap-2">
      <div
        style={{
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          backgroundColor: props.data.color,
          flexShrink: 0,
          marginRight: "8px",
        }}
      />
      <span>{props.data.label}</span>
    </div>
  </components.Option>
);

const StatusSingleValue = (props: SingleValueProps<StatusOptionType>) => (
  <components.SingleValue {...props}>
    <div className="flex items-center gap-2">
      <div
        style={{
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          backgroundColor: props.data.color,
          flexShrink: 0,
          marginRight: "8px",
        }}
      />
      <span>{props.data.label}</span>
    </div>
  </components.SingleValue>
);

const getStatusColor = (status: string | null): string => {
  switch (status?.toLowerCase()) {
    case "pending":
      return "#ffa500"; // orange
    case "in_progress":
      return "#ffff00"; // yellow
    case "success":
      return "#00aa00"; // green
    case "failed":
      return "#cc0000"; // red
    case "error":
      return "#cc0000"; // red
    default:
      return "#666666"; // gray
  }
};

const JobTableModal = ({
  isOpen,
  onClose,
  initialData,
}: JobTableModalProps) => {
  const { constants } = useConstants();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<JobFormData>({
    defaultValues: {
      status: "",
      status_message: "",
    },
  });

  const [patchBackgroundJob, { isLoading }] =
    usePatchBackgroundJobByIdMutation();

  useEffect(() => {
    if (isOpen && initialData) {
      reset({
        status: initialData.status || "",
        status_message: initialData.status_message || "",
      });
    } else if (isOpen && !initialData) {
      reset({
        status: "",
        status_message: "",
      });
    }
  }, [isOpen, initialData, reset]);

  const onSubmit = async (data: JobFormData) => {
    if (!initialData) return;

    try {
      await patchBackgroundJob({
        id: initialData.id.toString(),
        data: {
          status: data.status,
          status_message: data.status_message,
        },
      }).unwrap();

      KaboodleToaster.success("Background job updated successfully");
      onClose();
    } catch (err) {
      console.error("Failed to update background job:", err);
      if (hasApiError(err)) {
        KaboodleToaster.error(
          err.data?.message || "Failed to update background job",
        );
      } else {
        KaboodleToaster.error("Failed to update background job");
      }
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const rawStatusOptions = constants?.BACKGROUND_JOB_STATUS || [];
  const statusOptions: StatusOptionType[] = rawStatusOptions
    .filter(
      (option) => option.value !== null && option.label !== "-- Select --",
    )
    .map((option) => ({
      label: option.label,
      value: option.value,
      color: getStatusColor(option.value),
    }));

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={handleClose}
      footer={
        <div className="flex jc-c">
          <Button onClick={handleSubmit(onSubmit)} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
          <Button onClick={handleClose}>Cancel</Button>
        </div>
      }
    >
      <Card heading="Edit Background Job">
        <Controller
          name="status"
          control={control}
          rules={{ required: "Status is required" }}
          render={({ field: { onChange, value, ...field } }) => {
            const selectedOption = statusOptions.find(
              (option) => option.value?.toLowerCase() === value?.toLowerCase(),
            );

            return (
              <Select
                {...field}
                label="Status"
                options={statusOptions}
                value={selectedOption || null}
                errors={errors}
                components={{
                  Option: StatusOption,
                  SingleValue: StatusSingleValue,
                }}
                onChange={(option) => {
                  onChange(option ? (option as StatusOptionType).value : "");
                }}
                isClearable={false}
                isSearchable={false}
              />
            );
          }}
        />
        <Controller
          name="status_message"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              maxLength={255}
              ref={field.ref}
              label="Status Message"
              errors={errors}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};

export default JobTableModal;
